import type { PlaidIdentityAccount } from '@/lib/application-types'

export type PlaidAccountTypeDisplay = 'CHK' | 'SAV'

export type PlaidLinkDisplay = {
  institution_name: string
  account_name: string
  account_mask: string
  account_type: PlaidAccountTypeDisplay
  linked_at: string
}

export type SafePlaidAccountOption = {
  account_id: string
  name: string
  mask: string
  subtype: string
}

export type PlaidAccountsReadyPayload = {
  item_id: string
  accounts: SafePlaidAccountOption[]
}

export type ExchangePlaidFundingResult =
  | { linked: true; plaid_link: PlaidLinkDisplay }
  | { linked: false; needsAccountSelection: true; item_id: string; accounts: SafePlaidAccountOption[] }

type ExchangePlaidFundingParams = {
  applicationId: string
  publicToken?: string
  accountId?: string
  itemId?: string
  platform?: 'consumer' | 'business'
}

export function formatMaskedAccountNumber(accountMask: string | undefined | null): string {
  const mask = String(accountMask || '').trim()
  if (!mask) { return '••••' }
  const digits = mask.replace(/\D/g, '')
  const last4 = (digits || mask).slice(-4)
  return last4 ? `••••${last4}` : '••••'
}

export function mapPlaidLinkDisplayToLabel(link: PlaidLinkDisplay | null | undefined): string {
  if (!link) { return 'Linked account' }
  const mask = formatMaskedAccountNumber(link.account_mask)
  return [link.account_name, mask, link.account_type].filter(Boolean).join(' · ')
}

export async function exchangeAndLinkPlaidFunding(
  params: ExchangePlaidFundingParams
): Promise<ExchangePlaidFundingResult> {
  const body: Record<string, unknown> = {
    applicationId: params.applicationId,
    platform: params.platform || 'consumer',
  }
  if (params.publicToken) {
    body.publicToken = params.publicToken
  }
  if (params.accountId) {
    body.accountId = params.accountId
  }
  if (params.itemId) {
    body.itemId = params.itemId
  }

  const response = await fetch('/api/plaid/exchange-public-token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })

  const json = await response.json().catch(() => ({}))
  if (!response.ok || !json?.ok) {
    throw new Error(json?.error || 'Failed to link bank account via Plaid.')
  }

  const data = json.data || json
  if (data.linked === true && data.plaid_link) {
    return { linked: true, plaid_link: data.plaid_link as PlaidLinkDisplay }
  }

  if (data.needsAccountSelection && Array.isArray(data.accounts) && data.item_id) {
    return {
      linked: false,
      needsAccountSelection: true,
      item_id: String(data.item_id),
      accounts: data.accounts as SafePlaidAccountOption[],
    }
  }

  throw new Error('Unexpected Plaid funding link response.')
}

export async function finalizePlaidFundingAccountSelection(params: {
  applicationId: string
  itemId: string
  accountId: string
  platform?: 'consumer' | 'business'
}): Promise<PlaidLinkDisplay> {
  const result = await exchangeAndLinkPlaidFunding({
    applicationId: params.applicationId,
    itemId: params.itemId,
    accountId: params.accountId,
    platform: params.platform,
  })
  if (!result.linked) {
    throw new Error('Failed to finalize Plaid account selection.')
  }
  return result.plaid_link
}

export function hasPlaidLinkDisplay(
  link: PlaidLinkDisplay | null | undefined
): boolean {
  if (!link) { return false }
  return Boolean(
    String(link.institution_name || '').trim() ||
    String(link.account_name || '').trim()
  )
}

/** @deprecated Server-side only; retained for legacy type compatibility in approval UI. */
export type PlaidAccountDetails = {
  mask?: string
  name?: string
  institutionName?: string
  accountSubtype?: string
  routingNumber?: string
  accountNumber?: string
}

export function mapPlaidLinkDisplayFromSummary(
  plaidLink: {
    institution_name?: string
    account_name?: string
    account_mask?: string
    account_type?: PlaidAccountTypeDisplay
    linked_at?: string
  } | null | undefined
): PlaidLinkDisplay | null {
  if (!plaidLink) { return null }
  const institutionName = String(plaidLink.institution_name || '').trim()
  const accountName = String(plaidLink.account_name || '').trim()
  if (!institutionName && !accountName) { return null }
  return {
    institution_name: institutionName,
    account_name: accountName,
    account_mask: String(plaidLink.account_mask || '').trim(),
    account_type: plaidLink.account_type === 'SAV' ? 'SAV' : 'CHK',
    linked_at: String(plaidLink.linked_at || ''),
  }
}

export function buildPlaidIdentityData(
  accounts: PlaidIdentityAccount[],
  item: { item_id: string; institution_id?: string; institution_name?: string } | undefined,
  request_id: string | undefined,
  fetchedAt = new Date().toISOString()
) {
  return {
    accounts: accounts.map((account) => ({
      account_id: account.account_id,
      name: account.name,
      official_name: account.official_name,
      mask: account.mask,
      type: account.type,
      subtype: account.subtype,
      owners: account.owners || [],
    })),
    item,
    request_id,
    fetchedAt,
  }
}

export type FundingRowForPlaidReadiness = {
  amount?: string | number | null
  method?: string | null
  minimumDeposit?: number | null
}

export function parseFundingAmount(raw: string | number | undefined | null): number {
  if (typeof raw === 'number') { return Number.isFinite(raw) ? raw : 0 }
  if (!raw) { return 0 }
  const cleaned = String(raw).replace(/[^0-9.-]/g, '')
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : 0
}

/** Every funding row must have a valid amount and method before starting Plaid Link. */
export function isFundingReadyForPlaidLink(
  fundingRows: FundingRowForPlaidReadiness[],
): boolean {
  if (fundingRows.length === 0) { return false }

  return fundingRows.every((item) => {
    const amount = parseFundingAmount(item.amount)
    const minDeposit = item.minimumDeposit || 0
    if (!item.method || item.method === '') { return false }
    if (!item.amount || Number.isNaN(amount) || amount <= 0) { return false }
    if (amount < minDeposit) { return false }
    return true
  })
}
